import React, { useEffect, useRef } from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { useAppDispatch, useAppSelector } from "../../hooks";
import ChatComposer from "../chat/chat-composer";
import useIsPhone from "../files/use-is-phone";
import { MessageText } from "./message-text";
import { useAssistantSocket, subscribeToAssistant } from "./use-assistant-socket";
import {
  addLocalUserMessage,
  close,
  beginTurn,
  newConversation,
  receiveFailed,
  sendPrompt,
  setCompany,
  startConversation,
} from "./assistant.slice";

const ASSISTANT_NAME = "Tomas";
const MAX_PROMPT = 2000;

/**
 * Floating assistant window. Mounted alongside the mini-chat strip so it is
 * available on every company page (Dashboard.tsx).
 */
export const AssistantWidget: React.FC = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { companyId } = useParams<{ companyId: string }>();
  const { isOpen, conversationId, messages, isThinking, activeTool, error, epoch } =
    useAppSelector((state) => state.assistantReducer);
  // v1 is Master-only server-side; without this every employee would see a
  // launcher that only ever answers 403.
  const role = useAppSelector((state) => state.authReducer.role);
  const isMaster = !!role?.permissionsgroups?.includes("Master");
  const scrollerRef = useRef<HTMLDivElement | null>(null);
  const isPhone = useIsPhone();

  useAssistantSocket(companyId, conversationId);

  // Assistant state is per company — switching must not leave the previous
  // company's thread on screen.
  useEffect(() => {
    dispatch(setCompany(companyId || null));
  }, [companyId, dispatch]);

  // Streamed deltas mutate the last bubble's text rather than adding messages,
  // so track total content length or long replies grow off-screen.
  const renderedLength = messages.reduce((sum, m) => sum + m.content.length, 0);
  useEffect(() => {
    const scroller = scrollerRef.current;
    if (scroller) scroller.scrollTop = scroller.scrollHeight;
  }, [messages.length, renderedLength, isThinking]);

  useEffect(() => {
    if (error) toast.error(t("assistant_error", { defaultValue: "Assistant failed" }));
  }, [error, t]);

  const handleSend = async (content: string) => {
    if (!companyId) return;
    dispatch(addLocalUserMessage(content));
    // Disable the composer for the whole turn, including thread creation.
    dispatch(beginTurn());

    try {
      // Create the thread and join its room BEFORE asking, otherwise the first
      // answer is already complete by the time we could subscribe.
      let threadId = conversationId;
      if (!threadId) {
        const created = await dispatch(
          startConversation({ companyId, epoch })
        ).unwrap();
        threadId = created.conversation.id;
      }
      await subscribeToAssistant(companyId, threadId);

      await dispatch(
        sendPrompt({ companyId, conversationId: threadId, content, epoch })
      ).unwrap();
    } catch {
      // ChatComposer swallows rejections assuming the caller reports them, so a
      // failure before sendPrompt would otherwise leave the optimistic bubble
      // with no answer, no error and a disabled composer.
      // Tagged with the epoch this turn started on: a reset while it was in
      // flight must not surface the failure on the new thread.
      dispatch(receiveFailed({ errorCode: "assistant_failed", epoch }));
    }
  };

  // On a phone the panel covers the page, so following an in-app link would
  // leave the answer hiding the very record it points to. (A link to another
  // site opens a new tab and leaves the panel alone.)
  const closeOnPhoneLinkTap = (e: React.MouseEvent) => {
    const tappedAppLink = (e.target as HTMLElement).closest("a:not([target=_blank])");
    if (isPhone && tappedAppLink) dispatch(close());
  };

  // The entry point lives in the header (AssistantLauncher); this component is
  // just the panel, so it renders nothing until opened.
  if (!isMaster || !isOpen) {
    return null;
  }

  return (
    // Shrinks to fit a small screen: the container around it adds 1rem of space on every side
    // and sits 4rem down, under the header bar.
    <div className="pointer-events-auto flex h-[min(460px,calc(100dvh-6rem-var(--phone-bar-height,0px)))] w-[min(360px,calc(100vw-2rem))] flex-col overflow-hidden rounded-lg bg-gray-900 text-gray-100 shadow-2xl">
      <header className="flex items-center justify-between border-b border-gray-700 px-3 py-2 max-md:py-0">
        <div className="flex items-center gap-2">
          <span className="flex h-6 w-6 items-center justify-center rounded-full bg-indigo-600 text-xs font-semibold">
            T
          </span>
          <span className="text-sm font-medium">{ASSISTANT_NAME}</span>
        </div>
        <div className="flex items-center gap-3 text-xs">
          <button
            type="button"
            className="text-gray-400 hover:text-gray-200 disabled:opacity-40 max-md:min-h-11 max-md:px-2"
            disabled={isThinking}
            onClick={() => dispatch(newConversation())}
          >
            {t("assistant_new_conversation", { defaultValue: "New chat" })}
          </button>
          <button
            type="button"
            className="text-gray-400 hover:text-gray-200 max-md:min-h-11 max-md:min-w-11 max-md:text-base"
            onClick={() => dispatch(close())}
          >
            ✕
          </button>
        </div>
      </header>

      <div
        ref={scrollerRef}
        className="flex-1 space-y-3 overflow-y-auto px-3 py-3"
        onClick={closeOnPhoneLinkTap}
      >
        {messages.length === 0 && (
          <p className="mt-6 text-center text-xs text-gray-400">
            {t("assistant_intro", {
              defaultValue:
                "Ask me to find a record, or why the system won't let you do something.",
            })}
          </p>
        )}

        {messages.map((message) => (
          <div
            key={message.id}
            className={message.role === "user" ? "flex justify-end" : "flex justify-start"}
          >
            <div
              className={
                "max-w-[85%] break-words rounded-lg px-3 py-2 text-sm " +
                (message.role === "user"
                  // The employee's own text is never markdown, so it keeps the
                  // line breaks they typed. Assistant messages get their line
                  // structure from the renderer instead.
                  ? "whitespace-pre-wrap bg-indigo-600 text-white"
                  : "bg-gray-800 text-gray-100")
              }
            >
              {message.role === "user" ? (
                message.content
              ) : (
                <MessageText
                  content={message.content}
                  streaming={message.status === "streaming"}
                />
              )}
            </div>
          </div>
        ))}

        {isThinking && (
          <p className="text-xs italic text-gray-400">
            {activeTool
              ? t("assistant_searching", { defaultValue: "Searching…" })
              : t("assistant_thinking", { defaultValue: "Thinking…" })}
          </p>
        )}
      </div>

      <div className="border-t border-gray-700">
        <ChatComposer
          compact
          hideAttachment
          disabled={isThinking}
          maxLength={MAX_PROMPT}
          placeholder={t("assistant_placeholder", { defaultValue: "Ask Tomas…" })}
          onSend={handleSend}
          onFileSelected={() => undefined}
        />
        <p className="px-3 pb-2 text-[10px] text-gray-500">
          {t("assistant_disclaimer", {
            defaultValue: "Read-only. Answers come from your company's data — verify before acting.",
          })}
        </p>
      </div>
    </div>
  );
};

export default AssistantWidget;
